/* ----------------------------------------------------------------------------
   M1 · Courier Codes Master  (route /supply/courier-codes)
   Live-backend version: rows come from POST courierPartner/search (is_deleted
   eq '0', fetched in offset/limit pages) joined client-side with
   GET courierPartner/parent/list. Agreement badge + expiry are derived lazily
   from GET .../agreement/timeline/{parentId} — fetched at most ONCE per parent
   as its rows become visible (never one call per row upfront).
   KPI strip + SupplyTable (colored delivery-mode chips, service-type count
   cell, agreement + expiry pills), kebab row actions (deep dive, parent 360,
   commercials, rates & TAT, agreement history, edit, activate / deactivate
   with audit reason → remarks), Add/Edit Code drawer and Agreement History
   drawer. v6 §9.2 / M1.3 / QC-G
   ---------------------------------------------------------------------------- */
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import SettingsInputComponentOutlinedIcon from '@mui/icons-material/SettingsInputComponentOutlined'
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined'
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined'
import PowerSettingsNewOutlinedIcon from '@mui/icons-material/PowerSettingsNewOutlined'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import {Stack, Typography, Button, Chip} from '@mui/material'
import PageTitle from '../../../components/PageTitle'
import {Badge, KpiStrip, SupplyTable, ConfirmDialog, Modal, useToast, TableSkeleton, LoadErrorPanel, SupplyHero, HERO_BTN, HERO_BTN_SOLID, LogicalName, NOT_NAMED_HINT} from '../components'
import {initials, fmtDate} from '../components'
import {SERVICE_TYPES_BY_CODE, deliveryModesOf} from '../dummyData/serviceTypes' /* static label constants */
import {apiError} from '../supplyMeta'
import {
  useDashboardQuery,
  useListParentsQuery,
  useLazyAgreementTimelineQuery,
  useLazySearchCouriersQuery,
  useUpdateCourierMutation,
  useBackfillLogicalNamesMutation,
} from '../../../redux/supplyApiSlice'
import {pick, normalizeCourier, codeAgreementInfo} from './codeData'
import CodeFormDrawer from './CodeFormDrawer'
import AgreementHistoryDrawer from './AgreementHistoryDrawer'

const PAGE_SIZE = 20 /* server-side page size */
const EMPTY_LIST = [] /* stable empty ref — avoids a new [] each render (which would loop effects) */

/* ── Delivery-mode chips — colour map matches the mockup MODE_COLORS ─────── */

const MODE_COLORS = {
  Surface: '#6366F1',
  Air: '#1976D2',
  SDD: '#E53935',
  'Same-City NDD': '#00897B',
  'Intercity NDD': '#0288D1',
  '2–4h': '#F4511E',
  'Sub-2h': '#BF360C',
  'High Value': '#827717',
  Reverse: '#455A64',
}

function DeliveryModeChips({serviceTypes}) {
  const modes = deliveryModesOf(serviceTypes || [])
  if (!modes.length) return <span className='muted'>—</span>
  return (
    <>
      {modes.map((m) => {
        const color = MODE_COLORS[m] || '#78909C'
        return (
          <span
            key={m}
            className='tag'
            style={{
              background: color + '18',
              color,
              border: '1px solid ' + color + '44',
              fontSize: 11,
              padding: '2px 7px',
              borderRadius: 4,
              whiteSpace: 'nowrap',
              margin: '0 4px 2px 0',
            }}
          >
            {m}
          </span>
        )
      })}
    </>
  )
}

/* ── Service Types — count + tooltip listing full names (mockup cell) ────── */

function ServiceTypesCountCell({codes}) {
  const list = codes || []
  if (!list.length) return <span className='muted'>—</span>
  const names = list.map((sc) => {
    const st = SERVICE_TYPES_BY_CODE[sc]
    return st ? st.name : sc
  })
  return (
    <span title={names.join('\n')} style={{cursor: 'default'}}>
      <span className='mono' style={{fontSize: 12, fontWeight: 600}}>
        {list.length}
      </span>
      <span className='muted' style={{fontSize: 11, marginLeft: 4}}>
        {'type' + (list.length !== 1 ? 's' : '')}
      </span>
    </span>
  )
}

/* ── Agreement cells — lazy per-parent timeline ──────────────────────────── */
/* Rendered only for the table's visible page rows, so mounting one of these
   is the "row became visible" signal; ensureTimeline dedupes per parent. */

function AgreementCell({row, timelines, ensureTimeline, mode}) {
  useEffect(() => {
    if (row.parentId) ensureTimeline(row.parentId)
  }, [row.parentId, ensureTimeline])

  if (!row.parentId) return <span className='muted'>—</span>
  const t = timelines[row.parentId]
  if (!t) return <span className='muted'>…</span>
  if (t.error) return <span className='muted' title={t.error}>—</span>

  const info = codeAgreementInfo(t.items, row)
  if (mode === 'badge') return <Badge status={info.key} title={info.tip} />

  /* mode === 'expiry' — governing agreement's effectiveTo */
  if (!info.governing) return <span className='muted'>—</span>
  const a = info.governing.agreement || {}
  const to = pick(a, 'effectiveTo', 'effective_to')
  if (!to)
    return (
      <span
        title='No expiry date set on the governing agreement.'
        style={{color: 'var(--success)', fontSize: 12}}
      >
        Open-ended
      </span>
    )
  const days = info.governing.expiresInDays
  return (
    <span title={days != null ? 'Expires in ' + days + ' days.' : 'Governing agreement expires on this date.'}>
      {fmtDate(to)}
    </span>
  )
}

/* ══════════════════════════════════════════════════════════════════════════ */

export default function CourierCodesMaster() {
  const navigate = useNavigate()
  const toast = useToast()

  /* ── server-driven data ──────────────────────────────────────────────────
     KPIs come from the cached dashboard endpoint; parent names from the cached
     parent list; the TABLE itself is paginated server-side (20/page) so we never
     download the whole code list up-front (the old bulk-load was the slow part). */
  const {data: dashRaw, refetch: refetchDash} = useDashboardQuery()
  /* NB: no inline `= []` default — that allocates a fresh array every render while
     the query is pending, which would churn parentById → load → effect → re-render. */
  const {data: parentListRaw} = useListParentsQuery()
  const parents = parentListRaw || EMPTY_LIST /* shape consumed by CodeFormDrawer */

  const [fetchAgreementTimeline] = useLazyAgreementTimelineQuery()
  const [fetchCouriers] = useLazySearchCouriersQuery()
  const [updateCourier] = useUpdateCourierMutation()
  const [backfillLogicalNames, {isLoading: resyncing}] = useBackfillLogicalNamesMutation()

  /* Courier Code Display — "Resync logical names": confirm → POST backfill → result summary */
  const [resyncOpen, setResyncOpen] = useState(false)
  const [resyncResult, setResyncResult] = useState(null) /* {total, updated, unchanged, unnamed, unnamedAccountCodes, requiredSegments} */

  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  /* The search box lives INSIDE SupplyTable. Swapping the table for a skeleton on every server
     fetch unmounts it and wipes the half-typed query, so the skeleton is shown only until the
     first page has landed; later fetches just dim the mounted table. */
  const [loadedOnce, setLoadedOnce] = useState(false)

  /* server pagination / search / status-filter state (page is 1-based) */
  const [page, setPage] = useState(1)
  const [size, setSize] = useState(PAGE_SIZE)
  const [query, setQuery] = useState('') /* debounced search term (from SupplyTable) */
  const [hasNext, setHasNext] = useState(false) /* full page returned ⇒ a next page may exist */
  const [statusFilter, setStatusFilter] = useState('all') /* all | active | inactive */

  const [drawer, setDrawer] = useState(null) /* {existing: row|null} */
  const [historyCode, setHistoryCode] = useState(null)
  const [toggle, setToggle] = useState(null) /* {c, deact} */
  const [toggling, setToggling] = useState(false)

  /* per-parent agreement timelines: {parentId: {items, error?}} */
  const [timelines, setTimelines] = useState({})
  const timelineReqs = useRef({}) /* in-flight / done dedupe */

  const ensureTimeline = useCallback((parentId) => {
    if (!parentId || timelineReqs.current[parentId]) return
    timelineReqs.current[parentId] = true
    fetchAgreementTimeline(parentId)
      .unwrap()
      .then((items) => {
        setTimelines((prev) => ({...prev, [parentId]: {items: Array.isArray(items) ? items : []}}))
      })
      .catch((e) => {
        timelineReqs.current[parentId] = false /* allow retry on next mount */
        setTimelines((prev) => ({...prev, [parentId]: {items: [], error: apiError(e)}}))
      })
  }, [fetchAgreementTimeline])

  /* parentId → parent doc, for the name/status join on the current page */
  const parentById = useMemo(() => {
    const m = {}
    ;(parentListRaw || []).forEach((p) => {
      const id = pick(p, 'id', '_id')
      if (id != null) m[String(id)] = p
    })
    return m
  }, [parentListRaw])

  /* fetch ONE page from the backend whenever paging/search/status changes */
  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    const andfilter = [{field: 'is_deleted', operator: 'eq', value: '0'}]
    if (statusFilter !== 'all') andfilter.push({field: 'is_active', operator: 'eq', value: statusFilter === 'active' ? '1' : '0'})
    const body = {offset: (page - 1) * size, limit: size, skipCount: true, andfilter}
    if (query) {
      /* match the account code OR the derived logical name (e.g. "FWD-SURFACE" or "0.5KG") */
      const rx = '.*' + query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '.*'
      body.orfilter = [
        {field: 'cp_account_code', operator: 'like', value: rx},
        {field: 'logical_name', operator: 'like', value: rx},
      ]
    }
    fetchCouriers(body)
      .unwrap()
      .then((result) => {
        const docs = Array.isArray(result) ? result : []
        setHasNext(docs.length === size) /* full page ⇒ there may be more */
        setRows(
          docs.map((d) => {
            const r = normalizeCourier(d)
            const p = r.parentId ? parentById[String(r.parentId)] : null
            if (p && p.name) r.parent = p.name /* join by parentId; legacy `parent` string is the fallback */
            r.parentStatus = p ? p.status : null
            return r
          })
        )
      })
      .catch((e) => setError(apiError(e)))
      .finally(() => {
        setLoading(false)
        setLoadedOnce(true)
      })
  }, [page, size, query, statusFilter, parentById, fetchCouriers])

  useEffect(() => {
    load()
  }, [load])

  /* KPIs from the cached dashboard aggregate (whole dataset, not just this page) */
  const kpis = useMemo(() => {
    const codes = (dashRaw && dashRaw.codes) || {}
    const pp = (dashRaw && dashRaw.perParent) || []
    const totalCodes = codes.total || 0
    const active = codes.active || 0
    const parentsWithCodes = pp.filter((p) => (p.totalCodes || 0) > 0).length
    return [
      {label: 'Total Codes', value: totalCodes, icon: <SettingsInputComponentOutlinedIcon />, sub: 'all statuses'},
      {label: 'Active', value: active, icon: <CheckCircleOutlinedIcon />, sub: 'live in PACE', accent: '#16A34A'},
      {
        label: 'Inactive',
        value: Math.max(0, totalCodes - active),
        icon: <PowerSettingsNewOutlinedIcon />,
        sub: 'not allocating',
        accent: '#D97706',
      },
      {label: 'Parents', value: parentsWithCodes, icon: <GroupsOutlinedIcon />, sub: 'courier partners'},
    ]
  }, [dashRaw])

  /* ── activate / deactivate (PATCH courierPartner {id, isActive}) ────────── */

  const requestToggle = (c) => {
    const deact = c.isActive
    if (!deact && (!c.serviceTypes || !c.serviceTypes.length)) {
      toast('Cannot activate — assign at least one Delivery Service Type first.', 'error')
      return
    }
    setToggle({c, deact})
  }

  const applyToggle = ({c, deact}, reason) => {
    const patch = {id: c.id, isActive: !deact}
    if (reason) patch.remarks = reason /* audit reason — no dedicated field on codes */
    setToggling(true)
    updateCourier(patch)
      .unwrap()
      .then(() => {
        toast(c.code + (deact ? ' deactivated' : ' activated') + '.', 'success')
        load() /* refresh current page */
        refetchDash() /* refresh KPI counts */
      })
      .catch((e) => toast(apiError(e), 'error'))
      .finally(() => setToggling(false))
  }

  /* ── resync logical names (POST courierPartner/backfill-logical-name) ───── */

  const runResync = () => {
    backfillLogicalNames()
      .unwrap()
      .then((r) => {
        setResyncResult(r || {})
        toast('Logical names resynced — ' + (r && r.updated != null ? r.updated : 0) + ' updated.', 'success')
        load() /* refresh current page so the Logical Name column reflects the rewrite */
      })
      .catch((e) => {
        const status = e && e.status
        toast(status === 403 ? 'Only a Proship admin can resync logical names.' : apiError(e), 'error')
      })
  }

  /* ── edit commit (from CodeFormDrawer) — returns the API promise ────────── */
  /* Edit only: this page never creates codes (that is the Onboard New Partner wizard's job). */

  const commitCode = ({existing, doc}) =>
    updateCourier({id: existing.id, ...doc})
      .unwrap()
      .then(() => {
        toast(existing.code + ' updated.', 'success')
        setDrawer(null)
        load() /* refresh current page */
        refetchDash() /* refresh KPI counts */
      })
    /* rejection handled in the drawer (stays open, shows backend error) */

  /* ── cells that need navigate ───────────────────────────────────────────── */

  const goPartner = (e, parentId) => {
    e.preventDefault()
    e.stopPropagation() /* don't let the grid's row-click hijack to the code deep-dive */
    if (parentId) navigate('/supply/partners/' + parentId)
  }

  /* The name opens the code's deep-dive (same target as the row click); the Parent column is the
     link to the partner 360. Legacy codes have no parentId, so a partner link here went nowhere. */
  const goDeepDive = (e, code) => {
    e.preventDefault()
    e.stopPropagation()
    navigate('/supply/codes/' + encodeURIComponent(code))
  }

  const codeNameCell = (c) => (
    <div style={{display: 'flex', flexDirection: 'column', gap: 2}}>
      <a className='lnk' style={{fontWeight: 600, fontSize: 13.5}} href='#' onClick={(e) => goDeepDive(e, c.code)}>
        {c.display || c.code}
      </a>
      <span className='mono' style={{fontSize: 11, color: 'var(--neutral)'}}>
        {c.code}
      </span>
    </div>
  )

  const parentCell = (c) => (
    <span style={{display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'nowrap'}}>
      <span
        className='avatar'
        style={{width: 26, height: 26, fontSize: 10, borderRadius: 6, background: '#2065D1', flexShrink: 0}}
      >
        {initials(c.parent || '—')}
      </span>
      {c.parentId ? (
        <a className='lnk' href='#' onClick={(e) => goPartner(e, c.parentId)}>
          {c.parent || '—'}
        </a>
      ) : (
        <span title='Legacy code — not linked to an onboarded parent yet.'>{c.parent || '—'}</span>
      )}
    </span>
  )

  /* SupplyTable columns (server-paginated ⇒ sorting disabled) */
  const columns = [
    {key: 'display', label: 'Code', sortable: false, render: codeNameCell},
    {key: 'parent', label: 'Parent Courier', sortable: false, render: parentCell},
    {
      key: 'logicalName',
      label: 'Logical Name',
      sortable: false,
      render: (c) => <LogicalName value={c.logicalName} priority={c.priority} />,
      exportValue: (c) => c.logicalName || '',
    },
    {key: 'modes', label: 'Delivery Modes', sortable: false, render: (c) => <DeliveryModeChips serviceTypes={c.serviceTypes} />},
    {key: 'serviceTypes', label: 'Service Types', sortable: false, render: (c) => <ServiceTypesCountCell codes={c.serviceTypes} />},
    {key: 'agr', label: 'Agreement', sortable: false, render: (c) => <AgreementCell row={c} timelines={timelines} ensureTimeline={ensureTimeline} mode='badge' />},
    {key: 'agrExpiry', label: 'Agreement Expiry', sortable: false, render: (c) => <AgreementCell row={c} timelines={timelines} ensureTimeline={ensureTimeline} mode='expiry' />},
    {key: 'status', label: 'Status', sortable: false, render: (c) => <Badge status={c.status} />},
    {
      key: 'lastModified',
      label: 'Last Updated',
      sortable: false,
      render: (c) => (c.lastModified ? fmtDate(c.lastModified) : <span className='muted'>—</span>),
    },
  ]

  /* ── kebab row actions — mirrors the final mockup rowActions ────────────── */

  const rowActions = (c) => {
    const noSvc = !c.serviceTypes || !c.serviceTypes.length
    const isActive = c.isActive
    return [
      {
        label: 'Deep dive',
        icon: <VisibilityOutlinedIcon fontSize='small' />,
        onClick: () => navigate('/supply/codes/' + encodeURIComponent(c.code)),
      },
      {
        label: 'Parent 360',
        icon: <StorefrontOutlinedIcon fontSize='small' />,
        hidden: !c.parentId,
        onClick: () => navigate('/supply/partners/' + c.parentId),
      },
      {
        label: 'Rates & TAT',
        icon: <SyncOutlinedIcon fontSize='small' />,
        onClick: () => navigate('/supply/partners/update?code=' + encodeURIComponent(c.code)),
      },
      {
        label: 'Agreement History',
        icon: <HistoryOutlinedIcon fontSize='small' />,
        onClick: () => {
          if (c.parentId) ensureTimeline(c.parentId)
          setHistoryCode(c)
        },
      },
      {sep: true},
      {
        label: 'Edit',
        icon: <EditOutlinedIcon fontSize='small' />,
        onClick: () => setDrawer({existing: c}),
      },
      {sep: true},
      isActive
        ? {
            label: 'Deactivate',
            icon: <PowerSettingsNewOutlinedIcon fontSize='small' />,
            danger: true,
            onClick: () => requestToggle(c),
          }
        : noSvc
        ? {
            label: 'Activate (blocked — no service type)',
            icon: <LockOutlinedIcon fontSize='small' />,
            onClick: () => toast('Cannot activate — assign at least one Delivery Service Type first.', 'error'),
          }
        : {
            label: 'Activate',
            icon: <PowerSettingsNewOutlinedIcon fontSize='small' />,
            onClick: () => requestToggle(c),
          },
    ]
  }

  return (
    <>
      <PageTitle>{'Courier Codes'}</PageTitle>
      <SupplyHero
        icon={<SettingsInputComponentOutlinedIcon />}
        title='Courier Codes'
        subtitle='All courier account codes across partners — click a code for its deep-dive.'
        actions={
          /* No "Add Code" here: new account codes are created through the Onboard New Partner wizard
             (/supply/partners/onboard), which walks direction, service type, attributes and rates step by
             step. The flat drawer on this page is for EDITING an existing code only. */
          <>
            <Button
              variant='outlined'
              startIcon={<SyncOutlinedIcon />}
              onClick={() => setResyncOpen(true)}
              disabled={resyncing}
              title='Recompute the derived logical name for every live courier from its current attributes. Proship admin only.'
              sx={HERO_BTN}
            >
              {resyncing ? 'Resyncing…' : 'Resync Logical Names'}
            </Button>
            <Button variant='contained' disableElevation startIcon={<AddCircleOutlineIcon />} onClick={() => navigate('/supply/partners/onboard')} sx={HERO_BTN_SOLID}>
              Onboard Partner
            </Button>
          </>
        }
      />

      <KpiStrip cards={kpis} />

      {/* visible status filter bar */}
      <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap' useFlexGap sx={{mb: 1.5}}>
        <Typography sx={{fontSize: 11, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: 'text.secondary', mr: 0.5}}>Filter</Typography>
        {[{v: 'all', l: 'All'}, {v: 'active', l: 'Active'}, {v: 'inactive', l: 'Inactive'}].map((s) => {
          const active = statusFilter === s.v
          return (
            <Chip
              key={s.v}
              label={s.l}
              onClick={() => { setStatusFilter(s.v); setPage(1) }}
              sx={{height: 30, fontWeight: 600, fontSize: 12.5, cursor: 'pointer', border: 1, borderColor: active ? 'primary.main' : 'divider', bgcolor: active ? 'primary.main' : 'background.paper', color: active ? '#fff' : 'text.secondary', '&:hover': {bgcolor: active ? 'primary.dark' : 'action.hover'}}}
            />
          )
        })}
      </Stack>

      {loading && !loadedOnce ? (
        <TableSkeleton rows={8} />
      ) : error ? (
        <LoadErrorPanel message={error} onRetry={load} title='Couldn’t load courier codes' />
      ) : (
        <div style={{opacity: loading ? 0.55 : 1, transition: 'opacity .15s'}} aria-busy={loading}>
          <SupplyTable
            columns={columns}
            rows={() => rows}
            rowKey={(c) => c.id || c.code}
            searchPlaceholder='Search by code or logical name…'
            rowActions={rowActions}
            rowClick={(c) => navigate('/supply/codes/' + encodeURIComponent(c.code))}
            exportName='courier-codes'
            emptyText='No courier codes found.'
            emptyCta={{
              label: 'Onboard Partner',
              icon: <AddCircleOutlineIcon fontSize='small' />,
              onClick: () => navigate('/supply/partners/onboard'),
            }}
            server={{
              total: null /* count-agnostic: hasNext drives the Next button */,
              hasNext,
              page,
              onPage: setPage,
              size,
              onSize: (n) => {
                setSize(n)
                setPage(1)
              },
              query /* seeds the search box if the table ever remounts (e.g. after a load error) */,
              onSearch: (s) => {
                setQuery(s)
                setPage(1)
              },
            }}
          />
        </div>
      )}

      {/* Add / Edit Courier Code drawer (M1.3) */}
      <CodeFormDrawer
        open={!!drawer}
        existing={drawer ? drawer.existing : null}
        codes={rows}
        parents={parents}
        onClose={() => setDrawer(null)}
        onCommit={commitCode}
        toast={toast}
      />

      {/* Agreement History drawer (M0.11) */}
      <AgreementHistoryDrawer
        code={historyCode}
        timeline={historyCode && historyCode.parentId ? timelines[historyCode.parentId] : null}
        onClose={() => setHistoryCode(null)}
      />

      {/* Resync logical names — confirm */}
      <ConfirmDialog
        open={resyncOpen}
        onClose={() => setResyncOpen(false)}
        title='Resync logical names?'
        body='Every live courier code gets its logical name rebuilt from its current attributes using the naming rule. Only codes whose name changed are written.'
        bullets={[
          'Codes missing a required attribute stay unnamed — the result lists them and the attributes the rule needs',
          'Run this after a bulk attribute upload, or after the naming rule changes',
          'Safe to run repeatedly',
        ]}
        okLabel='Resync'
        onOk={() => {
          setResyncOpen(false)
          if (!resyncing) runResync()
        }}
      />

      {/* Resync logical names — result */}
      <Modal
        open={!!resyncResult}
        onClose={() => setResyncResult(null)}
        title='Logical names resynced'
        icon={<SyncOutlinedIcon />}
        width={560}
        actions={[{label: 'Close', primary: true}]}
      >
        {resyncResult ? (
          <>
            <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap sx={{mb: 2}}>
              <Chip size='small' label={(resyncResult.total ?? 0) + ' scanned'} sx={{fontWeight: 600}} />
              <Chip size='small' label={(resyncResult.updated ?? 0) + ' updated'} sx={{fontWeight: 600, bgcolor: '#e8f5e9', color: '#16a34a'}} />
              <Chip size='small' label={(resyncResult.unchanged ?? 0) + ' unchanged'} sx={{fontWeight: 600, bgcolor: '#f1f5f9', color: '#64748b'}} />
              <Chip size='small' label={(resyncResult.unnamed ?? 0) + ' not named'} sx={{fontWeight: 600, bgcolor: '#fff7ed', color: '#c2410c'}} />
            </Stack>
            {(resyncResult.unnamedAccountCodes || []).length ? (
              <>
                <Typography sx={{fontSize: 13, fontWeight: 600, mb: 0.75}}>
                  Still not named — missing one of {(resyncResult.requiredSegments || []).join(', ') || 'the required attributes'}:
                </Typography>
                <Stack direction='row' spacing={0.5} flexWrap='wrap' useFlexGap sx={{maxHeight: 240, overflowY: 'auto', p: 1, border: 1, borderColor: 'divider', borderRadius: 1}}>
                  {resyncResult.unnamedAccountCodes.map((code) => (
                    <Chip key={code} size='small' label={code} sx={{fontFamily: "'JetBrains Mono', monospace", fontSize: 11}} />
                  ))}
                </Stack>
                <Typography sx={{fontSize: 12, color: 'text.secondary', mt: 1}}>{NOT_NAMED_HINT}</Typography>
              </>
            ) : (
              <Typography sx={{fontSize: 13, color: 'text.secondary'}}>Every live courier code now has a logical name.</Typography>
            )}
          </>
        ) : null}
      </Modal>

      {/* Activate / Deactivate confirm (§2.5 audit → remarks) */}
      <ConfirmDialog
        open={!!toggle}
        onClose={() => setToggle(null)}
        title={toggle ? (toggle.deact ? 'Deactivate ' : 'Activate ') + toggle.c.code + '?' : ''}
        danger={toggle ? toggle.deact : false}
        needReason={toggle ? toggle.deact : false}
        body={
          toggle
            ? toggle.deact
              ? 'This will remove ' + toggle.c.code + ' from PACE allocation immediately.'
              : 'Activate ' + toggle.c.code + ' so PACE can allocate matching orders to it.'
            : ''
        }
        bullets={
          toggle && toggle.deact
            ? [
                'Code is excluded from PACE allocation',
                'Active shipments already on this code are flagged for manual review',
                'Agreement and rate-card links are preserved',
              ]
            : null
        }
        okLabel={toggle ? (toggle.deact ? 'Deactivate' : 'Activate') : ''}
        onOk={(reason) => {
          if (toggle && !toggling) applyToggle(toggle, reason)
          setToggle(null)
        }}
      />
    </>
  )
}
